import {DialogService, DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {Type} from '@angular/core';
import {concat, defer, Observable, of, timer} from 'rxjs';
import {map, switchMap, take} from 'rxjs/operators';
import {environment} from '../../environments/environment';

export {
    MOBILE_BREAKPOINT,
    TABLET_BREAKPOINT,
    DESKTOP_BREAKPOINT,
    PLANS_CAROUSEL_BREAKPOINT
} from '../services/viewport.service';

export interface EditableField {
    value: string;
    temp: string;
    editing: boolean;
}

export function openModal(
    dialogService: DialogService,
    modalComponent: any,
    options: Partial<DynamicDialogConfig> = {}
) {
    const defaultOptions: DynamicDialogConfig = {
        baseZIndex: 10000,
        contentStyle: { padding: '0 2rem 2rem 2rem', scrollbarWidth: 'none' },
        styleClass: 'dashboard-modal',
        maskStyleClass: 'dashboard-mask',
        dismissableMask: true,
        transitionOptions: '0ms',
        data: {},
    };

    const mergedOptions: DynamicDialogConfig = {
        ...defaultOptions,
        ...options,
        contentStyle: {
            ...defaultOptions.contentStyle,
            ...(options.contentStyle || {}),
        },
        data: {
            ...defaultOptions.data,
            ...(options.data || {}),
        },
    };

    return dialogService.open(modalComponent, mergedOptions);
}
export function openModalClose$<T = any>(
    dialogService: DialogService,
    modalComponent: Type<any>,
    options: Partial<DynamicDialogConfig> = {}
): { ref: DynamicDialogRef; close$: Observable<T | undefined> } {
    const ref = openModal(dialogService, modalComponent, options);
    const close$ = (ref.onClose as Observable<T | undefined>).pipe(take(1));
    return { ref, close$ };
}

export function openModalWithHeader(
    dialogService: DialogService,
    modalComponent: any,
    header: string,
    options: Partial<DynamicDialogConfig> = {}
) {
    const defaultOptions: DynamicDialogConfig = {
        baseZIndex: 10000,
        contentStyle: { padding: '0 1rem 1rem 1rem' },
        styleClass: 'dashboard-modal filter-modal',
        maskStyleClass: 'dashboard-mask',
        dismissableMask: true,
        data: {},
        header: header,
    };

    const mergedOptions: DynamicDialogConfig = {
        ...defaultOptions,
        ...options,
        contentStyle: {
            ...defaultOptions.contentStyle,
            ...(options.contentStyle || {}),
        },
        data: {
            ...defaultOptions.data,
            ...(options.data || {}),
        },
    };

    return dialogService.open(modalComponent, mergedOptions);
}

export function createEditableFields<T extends string>(
    initial: Record<T, string>
): Record<T, EditableField> {
    const result = {} as Record<T, EditableField>;
    for (const key in initial) {
        result[key as T] = {
            value: initial[key as T],
            temp: initial[key as T],
            editing: false,
        };
    }
    return result;
}

export function toggleEdit<T extends string>(
    fields: Record<T, EditableField>,
    field: T
) {
    const f = fields[field];
    if (f.editing) {
        const v = f.temp.trim();
        if (v) {
            f.value = v;
        }
    } else {
        f.temp = f.value;
    }
    f.editing = !f.editing;
}

export function toLoginReturnUrl(rawReturnUrl: string | null): string {
    if (!rawReturnUrl) {
        return environment.dashboardUrl;
    }
    try {
        const url = new URL(rawReturnUrl, window.location.origin);
        if (url.origin !== window.location.origin || url.pathname.startsWith('//')) {
            return environment.dashboardUrl;
        }
        url.searchParams.delete('email');
        return url.pathname + url.search + url.hash;
    } catch {
        return environment.dashboardUrl;
    }
}

const COPIED_DISPLAY_MS = 1800;

export function copyToClipboard$(text: string): Observable<boolean> {
    return defer(() => navigator.clipboard.writeText(text)).pipe(
        switchMap(() => concat(of(true), timer(COPIED_DISPLAY_MS).pipe(map(() => false))))
    );
}

export function formatCountLabel(
    count: number,
    singular: string,
    plural: string,
    zeroLabel: string,
    suffix?: string
): string {
    if (count === 0) {
        return zeroLabel;
    }

    const label = count === 1 ? singular : plural;
    const suffixText = suffix ? ` ${suffix}` : '';

    return `${count} ${label}${suffixText}`;
}

// A returnUrl is attacker-supplied whenever it reaches us from a link, so only
// the same-origin path survives: new URL() resolves it against our own origin
// and the origin is then discarded, which collapses https://evil.com/x,
// //evil.com/x and javascript:… to a local path. The email param is dropped
// because the upgrade page puts one there and it must not travel further.
export function sameOriginReturnUrl(rawReturnUrl: string): string {
    try {
        const url = new URL(rawReturnUrl, window.location.origin);
        url.searchParams.delete('email');
        return url.pathname + url.search + url.hash;
    } catch {
        return rawReturnUrl.split('?')[0];
    }
}

